/* Sukat global helpers. Vanilla JS, no dependencies. */
(() => {
  'use strict';

  const config = window.SukatConfig || { routes: {}, designMode: false };

  const debounce = (fn, wait = 200) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  async function postJSON(url, data) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(data)
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(json.description || json.message || response.statusText);
      error.status = response.status;
      error.data = json;
      throw error;
    }
    return json;
  }

  async function fetchSections(ids, url = window.location.pathname) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('sections', ids.join(','));
    const response = await fetch(target.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
    return response.json();
  }

  function sectionId(type) {
    const el = document.querySelector(`[data-section-type="${type}"]`);
    return el ? el.dataset.sectionId : null;
  }

  function replaceFromHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (next && current) current.replaceWith(next);
    return Boolean(next && current);
  }

  /* Dialogs: native showModal gives focus trapping and Escape to close. */
  const openers = new WeakMap();

  function openDialog(dialog, opener) {
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    openers.set(dialog, opener || document.activeElement);
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  }

  function closeDialog(dialog) {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    dialog.close();
  }

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      const opener = openers.get(dialog);
      if (opener && document.contains(opener) && typeof opener.focus === 'function') {
        opener.focus();
      }
      openers.delete(dialog);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-dialog-open]');
    if (openButton) {
      const dialog = document.getElementById(openButton.dataset.dialogOpen);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, openButton);
      }
      return;
    }

    const closeButton = event.target.closest('[data-dialog-close]');
    if (closeButton) {
      event.preventDefault();
      closeDialog(closeButton.closest('dialog'));
      return;
    }

    /* Backdrop click closes dialogs marked data-light-dismiss. Keyboard clicks have detail 0. */
    const dialog = event.target;
    if (
      dialog instanceof HTMLDialogElement &&
      dialog.open &&
      dialog.hasAttribute('data-light-dismiss') &&
      event.detail !== 0
    ) {
      const rect = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) dialog.close();
    }
  });

  /* Desktop nav dropdowns (native details): one open at a time, close on outside click and Escape. */
  const navDropdowns = () => document.querySelectorAll('[data-site-nav] details[open]');

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open || !details.closest('[data-site-nav]')) return;
      navDropdowns().forEach((other) => {
        if (other !== details) other.open = false;
      });
    },
    true
  );

  document.addEventListener('click', (event) => {
    navDropdowns().forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  document.addEventListener('focusout', (event) => {
    const details = event.target.closest && event.target.closest('[data-site-nav] details[open]');
    if (details && event.relatedTarget && !details.contains(event.relatedTarget)) details.open = false;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    navDropdowns().forEach((details) => {
      details.open = false;
      if (details.contains(document.activeElement)) details.querySelector('summary').focus();
    });
  });

  /* Links that point at a collapsed <details> section open it before scrolling there. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-open-details]');
    if (!link) return;
    const target = document.getElementById(link.dataset.openDetails);
    if (!(target instanceof HTMLDetailsElement)) return;
    event.preventDefault();
    target.open = true;
    target.scrollIntoView({ block: 'start' });
    target.querySelector('summary').focus({ preventScroll: true });
  });

  /* Cart icon refresh. Phase 6 dispatches cart:updated with { sections } from the cart AJAX response. */
  document.addEventListener('cart:updated', (event) => {
    const sections = event.detail && event.detail.sections;
    const id = sectionId('header');
    if (!sections || !id || !sections[id]) return;
    replaceFromHTML(sections[id], '[data-cart-icon]');
  });

  /* Live header height for sticky offsets; 0 when the header is not sticky. */
  let headerObserver = null;

  function trackHeader() {
    const wrapper = document.querySelector('.header-wrapper');
    if (headerObserver) headerObserver.disconnect();
    if (!wrapper || !('ResizeObserver' in window)) return;
    const setHeight = () => {
      const sticky = window.getComputedStyle(wrapper).position === 'sticky';
      const height = sticky ? Math.round(wrapper.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--header-live-h', `${height}px`);
    };
    headerObserver = new ResizeObserver(setHeight);
    headerObserver.observe(wrapper);
    setHeight();
  }

  trackHeader();
  window.matchMedia('(min-width: 990px)').addEventListener('change', trackHeader);
  document.addEventListener('shopify:section:load', trackHeader);

  window.Sukat = Object.freeze({
    config,
    debounce,
    postJSON,
    fetchSections,
    sectionId,
    replaceFromHTML,
    openDialog,
    closeDialog
  });
})();
